/**
 * Live Heart-Rate Widget — sits inside the Live Workout column.
 *
 * States rendered
 *  • Unsupported (iOS Safari) — friendly upgrade nudge
 *  • Idle       — "Connect Heart Rate Monitor" CTA
 *  • Requesting — spinner + "waiting for browser picker"
 *  • Connecting — spinner + "handshaking with device"
 *  • Streaming  — live BPM (pulse-animated), HR zone bar, calories, timer
 *  • Error      — inline error + retry
 *
 * Also POSTs a session summary to `/api/hr-sessions` when the user
 * clicks "End & Save Session" so we can later show a "Recent HR Sessions"
 * dashboard.
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Bluetooth, HeartPulse, Zap, Loader2, AlertTriangle, X, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { useHeartRateMonitor } from '../../hooks/useHeartRateMonitor';
import { useAuth } from '../../context/AuthContext';

const zoneFor = (bpm, maxHr) => {
  if (!bpm || !maxHr) return null;
  const pct = bpm / maxHr;
  if (pct < 0.6) return { key: 1, name: 'Warm-Up', color: 'text-sky-400', bar: 'from-sky-500/70 to-sky-400' };
  if (pct < 0.7) return { key: 2, name: 'Fat Burn', color: 'text-emerald-400', bar: 'from-emerald-500/70 to-emerald-400' };
  if (pct < 0.8) return { key: 3, name: 'Cardio', color: 'text-amber-300', bar: 'from-amber-500/70 to-amber-300' };
  if (pct < 0.9) return { key: 4, name: 'Threshold', color: 'text-orange-400', bar: 'from-orange-500/70 to-orange-400' };
  return { key: 5, name: 'Max Effort', color: 'text-red-400', bar: 'from-red-600 to-red-400' };
};

const formatDuration = (sec) => {
  const m = Math.floor(sec / 60).toString().padStart(2, '0');
  const s = (sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

export const HeartRateWidget = ({ workoutId, workoutTitle, onSaved }) => {
  const { user, api } = useAuth();
  const age = user?.age || 30;
  const weightLbs = user?.weight || null;
  const maxHr = 220 - age;

  const hrm = useHeartRateMonitor({ weightLbs, age });
  const [saving, setSaving] = useState(false);

  // Feature-unsupported (iOS Safari, older browsers)
  if (!hrm.supported) {
    return (
      <div
        className="mt-6 bg-card border border-primary/25 rounded-3xl p-5"
        data-testid="hr-widget-unsupported"
      >
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 shrink-0 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Bluetooth className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h4 className="font-heading text-sm uppercase tracking-wider text-white">Heart Rate Monitor</h4>
            <p className="text-muted-foreground text-xs mt-1 leading-relaxed">
              Web Bluetooth isn&apos;t supported on this browser. Open Ascend &amp; Surpass in
              <span className="text-primary"> Chrome or Edge on Android/desktop</span> to
              connect a compatible smartwatch or HR strap for live BPM &amp; calories.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const zone = zoneFor(hrm.bpm, maxHr);
  const pct = hrm.bpm ? Math.min(100, (hrm.bpm / maxHr) * 100) : 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api().post('/hr-sessions', {
        workout_id: workoutId || null,
        workout_title: workoutTitle || null,
        device_name: hrm.deviceName || null,
        duration_seconds: hrm.durationSec,
        avg_bpm: hrm.avgBpm,
        max_bpm: hrm.maxBpm,
        calories_estimated: hrm.calories,
      });
      toast.success(`Saved · ${hrm.avgBpm} avg BPM · ${hrm.calories} kcal`);
      hrm.disconnect();
      if (typeof onSaved === 'function') onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save HR session');
    }
    setSaving(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 bg-gradient-to-br from-red-500/10 via-black/40 to-black/70 border border-red-500/25 rounded-3xl p-5"
      data-testid="hr-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <HeartPulse className={`h-4 w-4 ${hrm.status === 'streaming' ? 'text-red-400 animate-pulse' : 'text-primary'}`} />
          <h4 className="font-heading text-sm uppercase tracking-wider text-white">Live Heart Rate</h4>
        </div>
        {hrm.status === 'streaming' && (
          <span className="text-[10px] uppercase tracking-widest font-heading text-emerald-400 flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {hrm.deviceName}
          </span>
        )}
      </div>

      {hrm.status === 'idle' && (
        <div className="text-center py-2">
          <p className="text-muted-foreground text-xs mb-4 leading-relaxed">
            Pair any BLE heart-rate strap or smartwatch broadcasting HR
            (Polar, Wahoo, Garmin, Apple Watch via HRM app, COOSPO,&nbsp;etc.)
          </p>
          <Button
            onClick={hrm.connect}
            className="w-full h-12 rounded-2xl bg-red-500 text-white font-heading tracking-wider uppercase hover:bg-red-500/90"
            data-testid="hr-connect-btn"
          >
            <Bluetooth className="mr-2 h-4 w-4" />
            Connect Heart Rate
          </Button>
        </div>
      )}

      {(hrm.status === 'requesting' || hrm.status === 'connecting') && (
        <div className="flex items-center justify-center gap-3 py-6 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-sm">
            {hrm.status === 'requesting' ? 'Waiting for you to pick a device…' : 'Connecting…'}
          </span>
        </div>
      )}

      {hrm.status === 'error' && (
        <div className="py-3">
          <div className="flex items-start gap-2 text-red-400 text-xs mb-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span className="leading-snug">{hrm.error || 'Connection failed'}</span>
          </div>
          <Button
            onClick={hrm.connect}
            variant="outline"
            size="sm"
            className="w-full rounded-2xl border-red-500/40 text-red-300 hover:bg-red-500/10"
            data-testid="hr-retry-btn"
          >
            Retry
          </Button>
        </div>
      )}

      {hrm.status === 'streaming' && (
        <div>
          {/* Live BPM */}
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest font-heading text-muted-foreground">Current</p>
              <div className="flex items-baseline gap-1.5">
                <span
                  className={`font-heading text-5xl ${zone?.color || 'text-white'} ${hrm.bpm ? 'animate-pulse' : ''}`}
                  data-testid="hr-live-bpm"
                >
                  {hrm.bpm ?? '—'}
                </span>
                <span className="text-muted-foreground text-xs uppercase tracking-widest">BPM</span>
              </div>
            </div>
            {zone && (
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest font-heading text-muted-foreground">Zone</p>
                <p className={`font-heading text-sm uppercase tracking-wider ${zone.color}`}>
                  Z{zone.key} · {zone.name}
                </p>
              </div>
            )}
          </div>

          {/* HR-zone bar (% of max HR) */}
          <div className="relative h-2 bg-black/60 border border-white/10 rounded-full mb-4 overflow-hidden">
            <motion.div
              className={`h-full bg-gradient-to-r ${zone?.bar || 'from-primary/70 to-primary'}`}
              style={{ width: `${pct}%` }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.5 }}
            />
            {/* Zone tick markers at 60/70/80/90% */}
            {[60, 70, 80, 90].map((mark) => (
              <div
                key={mark}
                className="absolute top-0 bottom-0 w-px bg-white/25"
                style={{ left: `${mark}%` }}
              />
            ))}
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-black/40 border border-white/10 rounded-xl p-2">
              <p className="text-[9px] uppercase tracking-widest font-heading text-muted-foreground">Avg</p>
              <p className="font-heading text-white text-lg" data-testid="hr-avg">{hrm.avgBpm || '—'}</p>
            </div>
            <div className="bg-black/40 border border-white/10 rounded-xl p-2">
              <p className="text-[9px] uppercase tracking-widest font-heading text-muted-foreground">Max</p>
              <p className="font-heading text-red-300 text-lg" data-testid="hr-max">{hrm.maxBpm || '—'}</p>
            </div>
            <div className="bg-black/40 border border-white/10 rounded-xl p-2">
              <p className="text-[9px] uppercase tracking-widest font-heading text-muted-foreground">Time</p>
              <p className="font-heading text-white text-lg tabular-nums">{formatDuration(hrm.durationSec)}</p>
            </div>
          </div>

          {/* Calories */}
          <div className="flex items-center justify-between bg-black/40 border border-primary/20 rounded-md px-3 py-2 mb-3">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-[10px] uppercase tracking-widest font-heading text-muted-foreground">Calories burned</span>
            </div>
            <span className="font-heading text-primary text-xl" data-testid="hr-calories">{hrm.calories}</span>
          </div>
          {(!weightLbs || !age) && (
            <p className="text-[10px] text-muted-foreground text-center mb-3 leading-snug">
              Add your weight &amp; age on Profile for accurate calorie estimates.
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={saving || hrm.durationSec < 5}
              className="flex-1 bg-primary text-primary-foreground font-heading tracking-wider uppercase hover:bg-primary/90"
              data-testid="hr-save-btn"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              End &amp; Save
            </Button>
            <Button
              onClick={hrm.disconnect}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="hr-disconnect-btn"
              aria-label="Disconnect"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default HeartRateWidget;
